import { Link } from "react-router-dom";
import { Heart, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useShop } from "../context/ShopContext";
import { gbp } from "../lib/api";
import { imgV } from "../lib/content";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, toggleWishlist, wishlist } = useShop();
  const wished = wishlist.some((w) => w.id === product.id);
  const out = product.stock_status === "out_of_stock";

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative overflow-hidden border border-white/5 group-hover:border-gold/40 transition-colors duration-700 bg-ink-3">
        <Link to={`/product/${product.slug}`} className="block aspect-[4/5] overflow-hidden bg-white">
          {product.image ? (
            <img
              src={imgV(product.image)}
              alt={product.name}
              loading="lazy"
              className={`w-full h-full object-contain transition-transform duration-[1.2s] ease-out group-hover:scale-110 ${out ? "opacity-50" : ""}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-mist text-xs font-mono">Image coming soon</div>
          )}
        </Link>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
        {out && (
          <span className="absolute top-3 left-3 bg-ink/85 text-stone-mist text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-white/10">
            Sold out
          </span>
        )}
        {product.featured && !out && (
          <span className="absolute top-3 left-3 bg-gold/90 text-ink text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-medium">
            Featured
          </span>
        )}
        <button
          onClick={() => toggleWishlist(product)}
          data-testid={`wishlist-toggle-${product.slug}`}
          aria-label="Add to wishlist"
          className={`absolute top-3 right-3 w-9 h-9 flex items-center justify-center border backdrop-blur-md transition-colors duration-300 ${
            wished ? "bg-gold text-ink border-gold" : "bg-ink/50 text-ivory border-white/10 hover:border-gold hover:text-gold"
          }`}
        >
          <Heart className="w-4 h-4" fill={wished ? "currentColor" : "none"} />
        </button>
        {!out && product.product_type !== "voucher" && (
          <button
            onClick={() => addToCart(product)}
            data-testid={`quick-add-${product.slug}`}
            className="absolute bottom-0 inset-x-0 bg-gold text-ink py-3 text-[11px] uppercase tracking-[0.25em] font-medium flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-500 hover:bg-gold-glow"
          >
            <Plus className="w-4 h-4" /> Add to Basket
          </button>
        )}
      </div>
      <div className="pt-4 flex items-start justify-between gap-3">
        <div>
          <Link to={`/product/${product.slug}`} className="text-ivory font-light hover:text-gold transition-colors duration-300 leading-snug block">
            {product.name}
          </Link>
          <p className="text-stone-mist text-xs mt-1">{product.metal}</p>
        </div>
        <p className="font-mono text-sm text-gold whitespace-nowrap pt-0.5">
          {product.price > 0 ? (
            <>
              {product.price_prefix === "from" && <span className="text-[10px] uppercase tracking-wider text-stone-mist mr-1">from</span>}
              {gbp(product.price)}
            </>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-stone-mist">Price on Request</span>
          )}
        </p>
      </div>
    </motion.div>
  );
}
